import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";

/**
 * Sales targets — the one number the bookings comparison needs.
 *
 * WHY THIS IS A HOST SCREEN AND NOT THE GENERATED SERVICE VIEW. The bookings
 * tile originally linked to `Sales Targets Management`, the auto-generated
 * ServiceView. That destination cannot work: `@intrasync/react`'s
 * ServiceView.Body resolves its service against the LIBRARY's own
 * `serviceStoresConfig`, which ships as a hardcoded empty array, so
 * `setCurrentService`/`loadServiceData` are never called for any service in any
 * host. All 27 service-view-enabled stores in this app land on a screen that
 * can never load data. That is an upstream defect, reported separately.
 *
 * A tile that says "no target set" has to lead somewhere a target can actually
 * be set, so this is a small real screen instead.
 */
type AnyStore = any; // eslint-disable-line @typescript-eslint/no-explicit-any

export interface SalesTarget {
  id: string;
  period: string;
  amount: number;
  note?: string | null;
}

/** `YYYY-MM` for the current month — the period the dashboard compares against. */
export const currentPeriod = (): string => new Date().toISOString().slice(0, 7);

class SalesTargetsStore {
  rootStore: RootStore;

  targets: SalesTarget[] = [];
  loading = false;
  failed = false;
  saving = false;
  error: string | null = null;

  /** Draft for the add/edit row. */
  draftPeriod = currentPeriod();
  draftAmount = "";
  draftNote = "";
  editingId: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false }, { autoBind: true });
  }

  private get service(): AnyStore | undefined {
    return (this.rootStore as unknown as Record<string, AnyStore>).salesTargetsStore;
  }

  setDraftPeriod(v: string) { this.draftPeriod = v; }
  setDraftAmount(v: string) { this.draftAmount = v; }
  setDraftNote(v: string) { this.draftNote = v; }

  /** `2026-08` and nothing else — the API validates the same pattern. */
  get draftPeriodValid(): boolean {
    return /^\d{4}-(0[1-9]|1[0-2])$/.test(this.draftPeriod);
  }

  get draftAmountValid(): boolean {
    const n = Number(this.draftAmount);
    return this.draftAmount.trim() !== "" && Number.isFinite(n) && n >= 0;
  }

  get canSave(): boolean {
    return this.draftPeriodValid && this.draftAmountValid && !this.saving;
  }

  beginEdit(target: SalesTarget) {
    this.editingId = target.id;
    this.draftPeriod = target.period;
    this.draftAmount = String(target.amount);
    this.draftNote = target.note ?? "";
  }

  cancelEdit() {
    this.editingId = null;
    this.draftPeriod = currentPeriod();
    this.draftAmount = "";
    this.draftNote = "";
  }

  async loadData(): Promise<void> {
    const service = this.service;
    if (!service) {
      runInAction(() => { this.failed = true; });
      return;
    }
    runInAction(() => { this.loading = true; this.failed = false; this.error = null; });
    try {
      const result = await service.find({ query: { $sort: { period: -1 }, $limit: 100 } });
      const rows = Array.isArray(result) ? result : (result?.data ?? []);
      runInAction(() => { this.targets = rows as SalesTarget[]; });
    } catch (e) {
      runInAction(() => {
        this.failed = true;
        this.error = e instanceof Error ? e.message : String(e);
      });
    } finally {
      runInAction(() => { this.loading = false; });
    }
  }

  /**
   * Create or update. `companyId` is deliberately NOT sent — it is a SERVER
   * FIELD resolved from the signed-in user, the same contract the module
   * services use. Sending it from the client would be the one way to write a
   * target into the wrong tenant.
   */
  async save(): Promise<void> {
    const service = this.service;
    if (!service || !this.canSave) return;

    runInAction(() => { this.saving = true; this.error = null; });
    try {
      const payload = {
        period: this.draftPeriod,
        amount: Number(this.draftAmount),
        note: this.draftNote.trim() || null,
      };
      if (this.editingId) {
        await service.patch(this.editingId, payload);
      } else {
        await service.create(payload);
      }
      runInAction(() => { this.cancelEdit(); });
      await this.loadData();
    } catch (e) {
      runInAction(() => {
        this.error = e instanceof Error ? e.message : String(e);
      });
    } finally {
      runInAction(() => { this.saving = false; });
    }
  }

  async remove(id: string): Promise<void> {
    const service = this.service;
    if (!service) return;
    try {
      await service.remove(id);
      await this.loadData();
    } catch (e) {
      runInAction(() => {
        this.error = e instanceof Error ? e.message : String(e);
      });
    }
  }
}

export default SalesTargetsStore;
